import React from "react";
import { Link } from "react-router-dom";
import { APP_NAME, SUPPORT_EMAIL } from "../constants/expiry";
import { SentrilogIcon } from "../components/Icons";

const EFFECTIVE_DATE = "March 1, 2026";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-[#111827] mb-4">{title}</h2>
    <div className="text-[#374151] text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

interface CookieRow {
  name: string;
  provider: string;
  purpose: string;
  duration: string;
}

const CookieTable: React.FC<{ rows: CookieRow[] }> = ({ rows }) => (
  <div className="overflow-x-auto mt-3">
    <table className="w-full text-sm border border-[#E5E7EB] rounded-xl overflow-hidden">
      <thead className="bg-[#F3F4F6]">
        <tr>
          {["Cookie Name", "Provider", "Purpose", "Duration"].map((h) => (
            <th
              key={h}
              className="px-4 py-3 text-left font-semibold text-[#111827] text-xs uppercase tracking-wide border-b border-[#E5E7EB]"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={i}
            className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}
          >
            <td className="px-4 py-3 font-mono text-[#374151] border-b border-[#F3F4F6]">
              {r.name}
            </td>
            <td className="px-4 py-3 text-[#374151] border-b border-[#F3F4F6]">
              {r.provider}
            </td>
            <td className="px-4 py-3 text-[#374151] border-b border-[#F3F4F6]">
              {r.purpose}
            </td>
            <td className="px-4 py-3 text-[#374151] border-b border-[#F3F4F6]">
              {r.duration}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CookiePolicy: React.FC = () => (
  <div className="min-h-screen bg-[#F9FAFB]">
    {/* Nav */}
    <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
            <SentrilogIcon className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-[#111827] text-lg">{APP_NAME}</span>
        </Link>
        <Link
          to="/"
          className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
        >
          ← Back to home
        </Link>
      </div>
    </header>

    {/* Content */}
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-3xl font-extrabold text-[#111827] mb-2">
          Cookie Policy
        </h1>
        <p className="text-sm text-[#6B7280]">
          Effective date: {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] px-8 py-10">
        <p className="text-[#374151] text-sm leading-relaxed mb-8">
          This Cookie Policy explains how {APP_NAME} ("<strong>we</strong>", "
          <strong>our</strong>", or "<strong>us</strong>") uses cookies and
          similar tracking technologies on sentrilog.com and within the{" "}
          {APP_NAME} application (collectively, the "
          <strong>Service</strong>"). By using the Service, you consent to our
          use of cookies as described in this policy.
        </p>

        <Section title="1. What Are Cookies?">
          <p>
            Cookies are small text files that are placed on your device when you
            visit a website or use a web application. They are widely used to
            make websites work more efficiently, to remember your preferences,
            and to provide information to site owners about how visitors interact
            with their content.
          </p>
          <p>
            In addition to cookies, we may use similar technologies such as
            local storage, session storage, and pixel tags. This policy covers
            all such technologies collectively referred to as "cookies".
          </p>
        </Section>

        <Section title="2. Types of Cookies We Use">
          <p>
            We use the following categories of cookies:
          </p>

          <div className="mt-4 space-y-5">
            {/* Strictly necessary */}
            <div className="p-4 bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-block px-2 py-0.5 bg-[#16A34A] text-white text-xs font-semibold rounded-full">
                  Always Active
                </span>
                <h3 className="font-semibold text-[#111827] text-sm">
                  Strictly Necessary Cookies
                </h3>
              </div>
              <p className="text-sm text-[#374151]">
                These cookies are essential for the Service to function and
                cannot be switched off. They are set in response to actions you
                take, such as logging in, accepting invitations, or setting your
                preferences. You can set your browser to block or alert you about
                these cookies, but some parts of the Service will not work.
              </p>
            </div>

            {/* Functional */}
            <div className="p-4 bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-block px-2 py-0.5 bg-[#2563EB] text-white text-xs font-semibold rounded-full">
                  Functional
                </span>
                <h3 className="font-semibold text-[#111827] text-sm">
                  Functional Cookies
                </h3>
              </div>
              <p className="text-sm text-[#374151]">
                These cookies enable the Service to remember choices you make
                (such as your language preference, sidebar state, or dismissed
                banners) and provide enhanced, personalised features. They are
                set by us and are not used to track you across other websites.
              </p>
            </div>

            {/* Analytics */}
            <div className="p-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-block px-2 py-0.5 bg-[#D97706] text-white text-xs font-semibold rounded-full">
                  Analytics
                </span>
                <h3 className="font-semibold text-[#111827] text-sm">
                  Performance &amp; Analytics Cookies
                </h3>
              </div>
              <p className="text-sm text-[#374151]">
                These cookies allow us to count visits and understand how users
                interact with the Service, helping us measure and improve
                performance. All information collected is aggregated and
                therefore anonymous. We do not use analytics cookies to identify
                individual users.
              </p>
            </div>
          </div>
        </Section>

        <Section title="3. Cookies We Set">
          <p>
            Below is a list of the key cookies set by {APP_NAME}:
          </p>
          <CookieTable
            rows={[
              {
                name: "token",
                provider: APP_NAME,
                purpose:
                  "Stores your authentication JWT token to keep you logged in across sessions.",
                duration: "7 days",
              },
              {
                name: "sentrilog_beta_banner_v1",
                provider: APP_NAME,
                purpose:
                  "Remembers whether you have dismissed the beta announcement banner.",
                duration: "Persistent",
              },
              {
                name: "post_login_redirect",
                provider: APP_NAME,
                purpose:
                  "Temporarily stores the page you should be redirected to after completing authentication (e.g., accepting a team invite).",
                duration: "Session",
              },
              {
                name: "auto_accept_invite",
                provider: APP_NAME,
                purpose:
                  "Flags that an invite acceptance should be automatically triggered after login.",
                duration: "Session",
              },
            ]}
          />
        </Section>

        <Section title="4. Third-Party Cookies">
          <p>
            We may use a limited number of third-party services that set their
            own cookies. These services include:
          </p>
          <CookieTable
            rows={[
              {
                name: "__stripe_*",
                provider: "Stripe",
                purpose:
                  "Used for payment fraud prevention and to remember payment method preferences.",
                duration: "Varies",
              },
            ]}
          />
          <p className="mt-4">
            We do not use Google Analytics, Facebook Pixel, or other
            advertising/tracking networks. We do not share cookie data with
            advertisers.
          </p>
        </Section>

        <Section title="5. Local Storage">
          <p>
            In addition to cookies, we use the browser's{" "}
            <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-xs font-mono">
              localStorage
            </code>{" "}
            API to persist certain data on your device, including:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-xs font-mono">
                token
              </code>{" "}
              — Your authentication token (also stored as a cookie).
            </li>
            <li>
              <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-xs font-mono">
                user
              </code>{" "}
              — Your profile data (name, email, role) for fast UI rendering.
            </li>
            <li>
              <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-xs font-mono">
                sentrilog_beta_banner_v1
              </code>{" "}
              — Banner dismiss flag.
            </li>
          </ul>
          <p className="mt-3">
            You can clear local storage at any time via your browser's developer
            tools. Clearing local storage will log you out of the Service.
          </p>
        </Section>

        <Section title="6. How to Control Cookies">
          <p>
            You have several options to control or opt out of cookies:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>
              <strong>Browser settings</strong> — Most browsers allow you to
              view, block, or delete cookies. Refer to your browser's help
              documentation for instructions:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>
                  <a
                    href="https://support.google.com/chrome/answer/95647"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563EB] underline"
                  >
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a
                    href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563EB] underline"
                  >
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a
                    href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563EB] underline"
                  >
                    Apple Safari
                  </a>
                </li>
                <li>
                  <a
                    href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2563EB] underline"
                  >
                    Microsoft Edge
                  </a>
                </li>
              </ul>
            </li>
            <li>
              <strong>Strictly necessary cookies</strong> — These cannot be
              disabled as they are required for the Service to function. Blocking
              them may prevent you from logging in or using core features.
            </li>
          </ul>
          <p className="mt-3 p-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl text-[#92400E] text-xs">
            ⚠️ Blocking all cookies may prevent you from logging in to{" "}
            {APP_NAME}. We recommend allowing cookies from sentrilog.com for the
            best experience.
          </p>
        </Section>

        <Section title="7. Changes to This Policy">
          <p>
            We may update this Cookie Policy from time to time to reflect changes
            in the cookies we use or for legal, regulatory, or operational
            reasons. When we make significant changes, we will update the
            effective date at the top of this page. We encourage you to review
            this policy periodically.
          </p>
        </Section>

        <Section title="8. Contact Us">
          <p>
            If you have any questions about our use of cookies, please contact
            us:
          </p>
          <div className="mt-3 p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] text-sm">
            <p className="font-semibold text-[#111827]">{APP_NAME}</p>
            <p>
              Email:{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[#2563EB] underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p>Website: sentrilog.com</p>
          </div>
        </Section>
      </div>

      {/* Bottom links */}
      <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-[#6B7280]">
        <Link
          to="/privacy"
          className="hover:text-[#111827] transition-colors underline"
        >
          Privacy Policy
        </Link>
        <Link
          to="/terms"
          className="hover:text-[#111827] transition-colors underline"
        >
          Terms of Service
        </Link>
        <Link to="/" className="hover:text-[#111827] transition-colors">
          ← Back to home
        </Link>
      </div>
    </main>
  </div>
);

export default CookiePolicy;
